import React, { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';
import { resetAutoLocationSession } from '../services/locationSession';
import { clearSession, saveSession } from '../services/user';
import { setV2BearerToken } from '../services/client';
import { ensurePushTokenSynced, syncUserPushTokenAfterLogin } from '../services/pushSync';
import { setGa4UserId } from '../services/firebaseAnalytics';

const V2SessionContext = createContext(null);

export function useV2Session() {
  const ctx = useContext(V2SessionContext);
  if (!ctx) throw new Error('useV2Session must be used within V2SessionProvider');
  return ctx;
}

function deriveFromInitial(s) {
  if (s?.token && s?.mobile) {
    return { mobile: s.mobile, token: s.token, user: s.user ?? null };
  }
  return { mobile: null, token: null, user: null };
}

/**
 * initialSession — restored from storage by V2App boot.
 * Shape: { mobile, token, user, pushToken? } | null
 */
export function V2SessionProvider({ children, initialSession = null }) {
  const initial = deriveFromInitial(initialSession);
  const [mobile, setMobile] = useState(initial.mobile);
  const [token, setToken] = useState(initial.token);
  const [user, setUser] = useState(initial.user);

  useLayoutEffect(() => {
    const d = deriveFromInitial(initialSession);
    setMobile(d.mobile);
    setToken(d.token);
    setUser(d.user);
    setV2BearerToken(d.token);
    if (d.mobile) {
      setGa4UserId(d.mobile);
    }
  }, [initialSession]);

  const loggedIn = Boolean(token && mobile);

  const login = useCallback(async (sessionData) => {
    await saveSession(sessionData);
    setMobile(sessionData.mobile);
    setToken(sessionData.token);
    setUser(sessionData.user ?? null);
    setV2BearerToken(sessionData.token);
    if (sessionData.mobile) {
      setGa4UserId(sessionData.mobile);
      await syncUserPushTokenAfterLogin(sessionData.mobile);
      ensurePushTokenSynced({ mobile: sessionData.mobile, forceUserDoc: true });
    }
  }, []);

  const logout = useCallback(async () => {
    resetAutoLocationSession();
    await clearSession();
    setMobile(null);
    setToken(null);
    setUser(null);
    setV2BearerToken(null);
    setGa4UserId(null);
  }, []);

  const value = useMemo(
    () => ({ loggedIn, mobile, token, user, login, logout }),
    [loggedIn, mobile, token, user, login, logout]
  );

  return <V2SessionContext.Provider value={value}>{children}</V2SessionContext.Provider>;
}
